import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Row, Col, Tabs, Button, notification } from "antd";
import { SaveFilled } from '@ant-design/icons';
import { getAssignmentsAssetsTemplate, getAssignmentsControlsTemplate } from "../../API/assignments";
import { getPriorities, getTypes } from "../../API/controls";

import AssignmentControls from "./components/AssignmentControls/AssignmentControls";
import AssignmentAssets from "./components/AssignmentAssets/AssignmentAssets";

import WithPermission from "../Common/WithPermission/WithPermission";
import PageTitle from "../Common/PageTitle/PageTitle";

import { useAuth } from "../Context/AuthContext";

import './Assignment.css';

const { TabPane } = Tabs;

const Assignments = () => {
  // Permisos
  const { hasPermission, userData, logout } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Consulta permisos: Tiene rol asignado  
  const hasAnyRole = userData?.roles?.length > 0;
  // Consulta especifica de permisos
  const canAssignmentRead = userData ? hasPermission("assignment", "read") : false;
  const canAssignmentCreate = userData ? hasPermission("assignment", "create") : false;
  const canAssignmentUpdate = userData ? hasPermission("assignment", "update") : false;
  
  const [api, contextHolder] = notification.useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  // Capturar activo y dependencia
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get("tab");
  const [initialSelectionApplied, setInitialSelectionApplied] = useState(false);
  // Parámetros para el tab 1 (Assets > Controls)
  const assetNameFromUrl = searchParams.get("asset");
  const dependencyNameFromUrl = searchParams.get("dependency");
  // Parámetros para el tab 2 (Controls > Assets)
  const controlNameFromUrl = searchParams.get("control");
  // Estados para controlar si los datos ya fueron cargados
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [controlsLoaded, setControlsLoaded] = useState(false);

  // -------------------------- Sección de Assets --------------------------
  const [assets, setAssets] = useState([]);
  const [selectedAssetDependencies, setSelectedAssetDependencies] = useState(null);
  const [selectedDependency, setSelectedDependency] = useState(null);
  const [assignmentsControlsToAssets, setAssignmentsControlsToAssets] = useState({});
  const [assignmentHistory, setAssignmentHistory] = useState([]);

  // -------------------------- Sección de Controls --------------------------
  const [controlsStructure, setControlsStructure] = useState([]);
  const [selectedAssetControls, setSelectedAssetControls] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null);
  const [assignmentsDependenciesToControls, setAssignmentsDependenciesToControls] = useState({});
  const [assignmentDependenciesHistory, setAssignmentDependenciesHistory] = useState([]);
  
  // Estados generales - CAMBIO: Separar los loading states
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [loadingControls, setLoadingControls] = useState(false);

  // Función para obtener el template de assets
  const fetchAssignmentsAssetsTemplate = async () => {
    try {
      setLoadingAssets(true);
      const response = await getAssignmentsAssetsTemplate();
      
      const mappedAssets = response.map(asset => ({
        key: asset.code,
        title: asset.name,
        dependencies: asset.dependencies.map(dep => ({
          key: dep.code,
          name: dep.name,
          attributes: dep.atributes,
          type: dep.type,
          controls: dep.controls?.map(control => ({
            ...control,
            flag: control.flag ?? false,
          })) ?? [],
        })),
      }));
      setAssets(mappedAssets);

      // Aplicar selección inicial si hay parámetro en URL y no se ha aplicado antes
      if (assetNameFromUrl && !initialSelectionApplied) {
        const assetToSelect = mappedAssets.find(a => 
          a.title.toLowerCase() === assetNameFromUrl.toLowerCase()
        );
        
        if (assetToSelect) {
          setSelectedAssetDependencies(assetToSelect);
          
          // Caso 1: Si viene SOLO el activo (no hacer nada más)
          // Caso 2: Si viene también la dependencia
          if (dependencyNameFromUrl) {
            const dependencyToSelect = assetToSelect.dependencies.find(d => 
              d.name.toLowerCase() === dependencyNameFromUrl.toLowerCase()
            );
            if (dependencyToSelect) {
              setSelectedDependency(dependencyToSelect);
            }
          }
          
          setInitialSelectionApplied(true);
        }
      }
      
      const initialAssignments = {};
      mappedAssets.forEach(asset => {
        initialAssignments[asset.key] = {};
        asset.dependencies.forEach(dep => {
          initialAssignments[asset.key][dep.key] = {};
          dep.controls.forEach(control => {
            initialAssignments[asset.key][dep.key][control.code] = control.flag;
          });
        });
      });
      setAssignmentsControlsToAssets(initialAssignments);
      setAssetsLoaded(true);
    } catch (error) {
      api.error({ message: "Error al cargar el template de assets" });
    } finally {
      setLoadingAssets(false);
    }
  };

  // Función para obtener el template de controls
  const fetchAssignmentsControlsTemplate = async () => {
    try {
      setLoadingControls(true);
      const response = await getAssignmentsControlsTemplate();
      
      const mappedAssets = response.map(asset => {
        return {
          key: asset.code || '',
          title: asset.name || '',
          controls: Array.isArray(asset.controls) 
            ? asset.controls.map(control => {
                return {
                  key: control.code || '',
                  title: control.title || '',
                  type: control.type || {},
                  priority: control.priority || {},
                  description: control.description || '',
                  dependencies: Array.isArray(control.dependencies) 
                    ? control.dependencies.map(dep => ({
                        code: dep?.code || '',
                        name: dep?.name || '',
                        description: dep?.description || '',
                        flag: dep?.flag !== undefined ? dep.flag : false,
                        can_assign: dep?.can_assign ?? false,
                        can_unassign: dep?.can_unassign ?? false,
                      }))
                    : []
                };
              })
            : []
        };
      });

      setControlsStructure(mappedAssets);

      const initialAssignments = {};
      mappedAssets.forEach(asset => {
        initialAssignments[asset.key] = {};
        asset.controls.forEach(control => {
          initialAssignments[asset.key][control.key] = {};
          control.dependencies.forEach(dep => {
            initialAssignments[asset.key][control.key][dep.code] = dep.flag;
          });
        });
      });

      setAssignmentsDependenciesToControls(initialAssignments);
      setControlsLoaded(true);
    } catch (error) {
      console.error("Error loading controls template:", error);
      api.error({ 
        message: "Error al cargar el template de controles",
        description: error.message 
      });
    } finally {
      setLoadingControls(false);
    }
  };

  useEffect(() => {
    if (tabFromUrl === "2") {
      setActiveTabKey("2");
      fetchAssignmentsControlsTemplate();
    } else {
      fetchAssignmentsAssetsTemplate();
    }
  }, [location.search]);

  const [priorities, setPriorities] = useState([]);
  const [types, setTypes] = useState([]);

  // CAMBIO: Simplificar la carga de filtros usando Promise.all
  useEffect(() => {
    Promise.all([
      getPriorities().then(data => setPriorities(data)),
      getTypes().then(data => setTypes(data))
    ])
      .catch(error => {
        console.error("Error al obtener filtros:", error);
      });
  }, []);

  const handleTabChange = (key) => {
    setActiveTabKey(key);
    setSelectedAssetDependencies(null);
    setSelectedDependency(null);
    setSelectedAssetControls(null);
    setSelectedControl(null);

    // Actualizar la URL con el nuevo tab
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', key);
    
    // Limpiar parámetros específicos del tab anterior
    if (key === '1') {
      searchParams.delete('control');
    } else {
      searchParams.delete('asset');
      searchParams.delete('dependency');
    }
    
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  
    if (key === '2' && !controlsLoaded) {
      fetchAssignmentsControlsTemplate(); 
    } else if (key === '1' && !assetsLoaded) {
      fetchAssignmentsAssetsTemplate();
    }
  };

  // Boton guardado superior
  // Estado para las funciones de guardar
  const [saveFunctions, setSaveFunctions] = useState({
    tab1: () => {},
    tab2: () => {}
  });

  // Función para registrar la función de guardar del hijo
  const registerSaveFunction = (tabKey, func) => {
    setSaveFunctions(prev => ({
      ...prev,
      [`tab${tabKey}`]: func
    }));
  };

  // Función para manejar el guardado desde el padre
  const handleSaveFromParent = () => {
    if (activeTabKey === '1' && saveFunctions.tab1) {
      saveFunctions.tab1();
    } else if (activeTabKey === '2' && saveFunctions.tab2) {
      saveFunctions.tab2();
    }
  };
  
  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canAssignmentRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <Row justify="start" className="page-header">
          <Col span={16}>
            <PageTitle title={activeTabKey === "1" ? "Asignación de Controles" : "Asignación de Controles a Activos"} icon="assignment" />
          </Col>
        </Row>
        <Row justify="end" style={{ marginTop: "16px" }}>
          <Button
            icon={<SaveFilled />}
            className="save-type-button"
            style={{ marginBottom: 10 }}
            type="primary"
            onClick={handleSaveFromParent}
            disabled={
              (activeTabKey === '1' && assets.length === 0) ||
              (activeTabKey === '2' && controlsStructure.length === 0) ||
              !(
                isSuperuser ||
                (hasAnyRole && canAssignmentCreate || canAssignmentUpdate)
              )
            }
          >
            Guardar
          </Button>
        </Row>
        <Tabs className="assignment-tab" activeKey={activeTabKey} onChange={handleTabChange} centered>
          <TabPane tab="Activos > Controles" key="1">
            <AssignmentControls
              permits={{ isSuperuser, hasAnyRole, canAssignmentCreate, canAssignmentUpdate }}
              assets={assets}
              loading={loadingAssets}
              selectedAsset={selectedAssetDependencies}
              selectedDependency={selectedDependency}
              assignmentsControlsToAssets={assignmentsControlsToAssets}
              assignmentHistory={assignmentHistory}
              setSelectedAsset={setSelectedAssetDependencies}
              setSelectedDependency={setSelectedDependency}
              setAssignmentsControlsToAssets={setAssignmentsControlsToAssets}
              setAssignmentHistory={setAssignmentHistory}
              priorities={priorities}
              types={types}
              api={api}
              registerSaveFunction={(func) => registerSaveFunction('1', func)}
            />
          </TabPane>

          <TabPane tab="Controles > Activos" key="2">
            <AssignmentAssets
              permits={{ isSuperuser, hasAnyRole, canAssignmentCreate, canAssignmentUpdate }}
              assets={controlsStructure}
              loading={loadingControls}
              selectedAsset={selectedAssetControls}
              selectedControl={selectedControl}
              assignmentsDependenciesToControls={assignmentsDependenciesToControls}
              assignmentDependenciesHistory={assignmentDependenciesHistory}
              setSelectedAsset={setSelectedAssetControls}
              setSelectedControl={setSelectedControl}
              setAssignmentsDependenciesToControls={setAssignmentsDependenciesToControls}
              setAssignmentDependenciesHistory={setAssignmentDependenciesHistory}
              priorities={priorities}
              types={types}
              api={api}
              initialSearchTerm={controlNameFromUrl}
              registerSaveFunction={(func) => registerSaveFunction('2', func)}
            />
          </TabPane>
        </Tabs>
      </>
    </WithPermission>
  );
};

export default Assignments;